import React, { useState, useContext } from 'react';
import { LanguageContext } from '../../../App';
import { Modal } from '../../ui/Modal';
import { ChevronDownIcon, SearchIcon, CheckIcon, UserIcon, MapPinIcon, ClockIcon } from 'lucide-react';
interface AssignDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const AssignDriverModal: React.FC<AssignDriverModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Sample data
  const orders = [{
    id: 'ORD-7829',
    customer: 'Ahmed Al-Sabah',
    pickup: 'Salmiya, Block 12',
    destination: 'Kuwait City, Block 3',
    time: '10 mins ago'
  }, {
    id: 'ORD-7828',
    customer: 'Fatima Al-Qadiri',
    pickup: 'Hawally, Block 5',
    destination: 'Salmiya, Block 10',
    time: '25 mins ago'
  }, {
    id: 'ORD-7827',
    customer: 'Mohammed Al-Mutairi',
    pickup: 'Jabriya, Block 1',
    destination: 'Salmiya, Block 2',
    time: '45 mins ago'
  }];
  const drivers = [{
    id: 'DRV-1001',
    name: 'Mohammed Al-Sabah',
    status: 'available',
    location: 'Salmiya, Block 10',
    rating: 4.9,
    distance: '1.2 km'
  }, {
    id: 'DRV-1002',
    name: 'Ahmed Al-Enezi',
    status: 'available',
    location: 'Kuwait City, Block 3',
    rating: 4.7,
    distance: '2.5 km'
  }, {
    id: 'DRV-1003',
    name: 'Fatima Al-Qadiri',
    status: 'available',
    location: 'Hawally, Block 5',
    rating: 4.8,
    distance: '3.1 km'
  }];
  const filteredDrivers = drivers.filter(driver => driver.name.toLowerCase().includes(searchTerm.toLowerCase()) || driver.id.toLowerCase().includes(searchTerm.toLowerCase()) || driver.location.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle assignment
    // In a real app, this would send data to an API
    // Close the modal after submission
    onClose();
  };
  const getSelectedOrderDetails = () => {
    return orders.find(order => order.id === selectedOrder);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={language === 'en' ? 'Assign Driver to Order' : 'تعيين سائق للطلب'} size="lg" data-id="element-425">
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-426">
        {/* Order Selection */}
        <div data-id="element-427">
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-428">
            {language === 'en' ? 'Select Order' : 'اختر الطلب'}
          </label>
          <div className="relative" data-id="element-429">
            <select id="order" value={selectedOrder} onChange={e => setSelectedOrder(e.target.value)} className="appearance-none block w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" required data-id="element-430">
              <option value="" data-id="element-431">
                {language === 'en' ? 'Select an order' : 'اختر طلب'}
              </option>
              {orders.map(order => <option key={order.id} value={order.id} data-id="element-432">
                  {order.id} - {order.customer}
                </option>)}
            </select>
            <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-433" />
          </div>
        </div>
        {/* Order Details */}
        {selectedOrder && <div className="bg-gray-50 p-4 rounded-md" data-id="element-434">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-435">
              {language === 'en' ? 'Order Details' : 'تفاصيل الطلب'}
            </h4>
            <div className="space-y-2" data-id="element-436">
              {getSelectedOrderDetails() && <>
                  <div className="flex items-start" data-id="element-437">
                    <MapPinIcon size={16} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-438" />
                    <div data-id="element-439">
                      <div className="text-xs text-gray-500" data-id="element-440">
                        {language === 'en' ? 'Pickup Location' : 'موقع الاستلام'}
                      </div>
                      <div className="text-sm" data-id="element-441">
                        {getSelectedOrderDetails()?.pickup}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-442">
                    <MapPinIcon size={16} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-443" />
                    <div data-id="element-444">
                      <div className="text-xs text-gray-500" data-id="element-445">
                        {language === 'en' ? 'Destination' : 'الوجهة'}
                      </div>
                      <div className="text-sm" data-id="element-446">
                        {getSelectedOrderDetails()?.destination}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-447">
                    <ClockIcon size={16} className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-448" />
                    <div data-id="element-449">
                      <div className="text-xs text-gray-500" data-id="element-450">
                        {language === 'en' ? 'Order Time' : 'وقت الطلب'}
                      </div>
                      <div className="text-sm" data-id="element-451">
                        {getSelectedOrderDetails()?.time}
                      </div>
                    </div>
                  </div>
                </>}
            </div>
          </div>}
        {/* Driver Search */}
        <div data-id="element-452">
          <label htmlFor="driverSearch" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-453">
            {language === 'en' ? 'Search Available Drivers' : 'البحث عن السائقين المتاحين'}
          </label>
          <div className="relative" data-id="element-454">
            <input type="text" id="driverSearch" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={language === 'en' ? 'Search by name, ID or location' : 'البحث بالاسم أو الرقم أو الموقع'} className="block w-full px-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-455" />
            <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-456" />
          </div>
        </div>
        {/* Driver List */}
        <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-457">
          <div className="max-h-60 overflow-y-auto" data-id="element-458">
            {filteredDrivers.length > 0 ? filteredDrivers.map(driver => <div key={driver.id} onClick={() => setSelectedDriver(driver.id)} className={`p-3 border-b border-gray-200 last:border-0 flex items-center cursor-pointer ${selectedDriver === driver.id ? 'bg-congress-blue-50' : 'hover:bg-gray-50'}`} data-id="element-459">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3" data-id="element-460">
                    <UserIcon size={16} className="text-gray-500" data-id="element-461" />
                  </div>
                  <div className="flex-1" data-id="element-462">
                    <div className="flex justify-between" data-id="element-463">
                      <div className="text-sm font-medium" data-id="element-464">{driver.name}</div>
                      <div className="text-xs text-gray-500" data-id="element-465">
                        {driver.distance}
                      </div>
                    </div>
                    <div className="flex justify-between" data-id="element-466">
                      <div className="text-xs text-gray-500" data-id="element-467">{driver.id}</div>
                      <div className="text-xs" data-id="element-468">⭐ {driver.rating}</div>
                    </div>
                  </div>
                  {selectedDriver === driver.id && <CheckIcon size={16} className="text-congress-blue-600 ml-2" data-id="element-469" />}
                </div>) : <div className="p-4 text-center text-sm text-gray-500" data-id="element-470">
                {language === 'en' ? 'No drivers found' : 'لم يتم العثور على سائقين'}
              </div>}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3" data-id="element-471">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-472">
            {language === 'en' ? 'Cancel' : 'إلغاء'}
          </button>
          <button type="submit" disabled={!selectedOrder || !selectedDriver} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-congress-blue-600 hover:bg-congress-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-473">
            {language === 'en' ? 'Assign Driver' : 'تعيين السائق'}
          </button>
        </div>
      </form>
    </Modal>;
};